<template lang="html"> 
  <v-dialog
    v-model="dialog"
    max-width="1600px"
    transition="dialog-transition"
  >
    <template v-slot:activator="{ on }">
      <slot 
        :on="on" 
        name="activator"
      />    
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="green"
      class="mb-0"
    >
      <template v-slot:header>
        <v-card-title>
          <div class="px-2">
            <h4 class="title font-weight-light">
              Branch: {{ branch.branch_name }}
            </h4>
            <p class="category font-weight-thin">
              Skill transfer preview
            </p>
          </div>
          <v-spacer />
          <v-btn  
            text
            icon 
            color="white"
            @click="closeDialog"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
      </template>

      <div 
        data-simplebar 
        class="overflow"
      >
        <v-row>
          <v-col 
            cols="12"
            md="3"
            class="px-4"
          >
            <v-autocomplete
              v-model="employeeSkillFilter"
              :items="branchSkills"
              label="Employee Skill Filter"
              placeholder="Employee List Skill Filter"
              item-text="name"
              return-object
              background-color="white"
              hide-no-data
              hide-selected
              chips
              filled
              clearable
            >
              <template v-slot:selection="data">              
                <v-chip
                  :input-value="data.selected"
                  close
                  class="chip--select-multi"
                  @click:close="removeEmployeeSkillFilter(data.item)"
                >
                  <v-avatar left>
                    <img
                      :src="data.item.image_url"
                      alt="skill logo"
                    >
                  </v-avatar>
                  {{ data.item.name }} ({{ data.item.rating }})
                </v-chip>
              </template>

              <template v-slot:item="data">
                <template>
                  <v-list-item-avatar 
                    size="40px" 
                    tile
                  >
                    <img :src="data.item.image_url">
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>
                      <span class="pl-2 pb-0">                      
                        {{ data.item.name }}
                      </span>
                    </v-list-item-title>
                    
                    <v-list-item-subtitle>
                      <v-rating 
                        v-model="data.item.rating"
                        :size="15"
                        hover 
                      />
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </template>                
            </v-autocomplete>
          </v-col>

          <v-col 
            cols="12"
            md="7"
            class="px-4"
          >
            <v-autocomplete
              v-model="temporarySelectedEmployees"             
              :items="filteredEmployeesLeft"
              autofocus              
              label="Employees to add"
              placeholder="Employees to add list"
              item-text="person.name"
              return-object             
              background-color="white"
              clearable
              hide-no-data
              hide-selected
              chips
              multiple
              filled
            >
              <template v-slot:append-outer>
                <v-btn 
                  :disabled="!temporarySelectedEmployees.length>0"
                  class="mt-0"
                  color="success" 
                  @click="addToEmployeeList"
                >
                  <v-icon 
                    dark 
                    left
                  >
                    add
                  </v-icon>Add to Chart                 
                </v-btn>
              </template>
              <template 
                v-slot:item="data"
              >
                <template>
                  <v-list-item-avatar 
                    v-if="data.item.person && data.item.person.employee"
                    size="40px"
                  >
                    <img :src="data.item.person.employee.avatar_url">
                  </v-list-item-avatar>
                  <v-list-item-content @click="data.selected = true">
                    <v-list-item-title 
                      @click="data.selected = true" 
                    >
                      <span class="pl-2 pb-0">                      
                        {{ data.item.person.name.substring(0,20) }}
                      </span>
                    </v-list-item-title>

                    <v-list-item-subtitle v-if="employeeSkillFilter">
                      <v-rating 
                        :size="15"
                        :value="data.item.rating"
                        readonly
                      />
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </template> 

              <template v-slot:selection="data">              
                <v-chip
                  :input-value="data.selected"
                  close
                  class="chip--select-multi"
                  @click:close="removeTempSelectedEmployees(data.item)"
                >
                  <v-avatar
                    v-if="data.item.person && data.item.person.employee"
                    left
                  >
                    <img
                      :src="data.item.person.employee.avatar_url"
                      alt="skill logo"
                    >
                  </v-avatar>
                  {{ data.item.person.name.substring(0,7) }}
                </v-chip>
              </template>
            </v-autocomplete>            
          </v-col>

          <v-col 
            class="pt-4"
            md="1"
            cols="12"
          >
            <v-btn-toggle 
              v-model="chartSwitch"
              mandatory
              @change="addToEmployeeList"
            >
              <v-btn 
                value="bar"
                class="btn-group px-2 mr-0"
              >
                <span>Bar</span>
                <v-icon class="ml-2">
                  fas fa-chart-bar
                </v-icon>
              </v-btn>

              <v-btn 
                :disabled="disableRadar"
                value="radar"
                class="btn-group px-2"
              >
                <span>Radar</span>
                <v-icon class="ml-2">
                  fas fa-bullseye
                </v-icon>
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
        <v-row
          class="flex-nowrap no-gutter"
        > 
          <v-col
            cols="6"
            class="px-0 mx-0 pb-0"
          >
            <setup-branch-chart-bar-skill-change-overview 
              v-if="dialog"
              v-show="chartSwitch==='bar'"
              :highest-y-value="highestYValue"
              :labels="chartLabels"
              :data="barchart1Data" 
              movement="out"
            />

            <setup-branch-chart-radar-skill-change-overview 
              v-show="chartSwitch==='radar'"
              v-if="dialog" 
              :data="radar1Data"
              :labels="radarLabels"
            />

            <v-autocomplete
              v-model="selectedEmployees"
              :items="selectedEmployees"              
              label="Selected Employee List"
              placeholder="Selected Employee List"
              item-text="person.name"
              return-object             
              background-color="white"
              clearable
              hide-no-data
              hide-selected
              chips
              multiple
              filled
            >
              <template v-slot:selection="data">              
                <v-chip
                  :input-value="data.selected"
                  close
                  class="chip--select-multi"
                  @click:close="removeSelectedEmployees(data.item)"
                >
                  <v-avatar
                    v-if="data.item.person && data.item.person.employee"
                    left
                  >
                    <img                      
                      :src="data.item.person.employee.avatar_url"
                      alt="skill logo"
                    >
                  </v-avatar>
                  {{ data.item.person.name.substring(0,7) }}
                </v-chip>
              </template>
            </v-autocomplete>
          </v-col>
          
          <v-col
            cols="6"
            class="pb-0"
          >
            <setup-branch-chart-bar-skill-change-overview 
              v-if="dialog"
              v-show="chartSwitch==='bar'"
              :highest-y-value="highestYValue"
              :labels="chartLabels"
              :data="barchart2Data" 
              movement="in"
            />

            <setup-branch-chart-radar-skill-change-overview 
              v-show="chartSwitch==='radar'"
              v-if="dialog" 
              :data="radar2Data"
              :labels="radarLabels"
            />

            <v-autocomplete
              v-model="secondaryBranch"
              :items="branchList"              
              label="Recieving Branch"
              placeholder="Select Recieving Branch"
              item-text="branch_name"
              return-object
              background-color="white"
              hide-no-data
              hide-selected
              chips
              filled
              clearable
            />
          </v-col>
        </v-row>
              
        <v-row>
          <v-col
            cols="12"
            class="mt-0 pt-0"
          >
            <v-autocomplete
              v-model="chartSkillFilter"
              :items="aggregateSelectedEmployeeSkills"              
              label="Chart Skill Filter"
              placeholder="Filter Chart"
              item-text="name"
              return-object             
              background-color="white"
              clearable
              hide-no-data
              hide-selected
              chips
              multiple
              filled
            >
              <template v-slot:selection="data">              
                <v-chip
                  :input-value="data.selected"
                  close
                  class="chip--select-multi"
                  @click:close="removeSelectedChartSkillFilter(data.item)"
                >
                  <v-avatar left>
                    <img
                      :src="data.item.image_url"
                      alt="skill logo"
                    >
                  </v-avatar>
                  {{ data.item.name }}
                </v-chip>
              </template>

              <template v-slot:item="data">
                <template>
                  <v-list-item-avatar 
                    size="40px" 
                    tile
                  >
                    <img :src="data.item.image_url">
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title v-html="data.item.name" />
                    <!-- <v-list-item-subtitle v-html="data.item.skill_category.name"/> -->
                  </v-list-item-content>
                </template>
              </template>                
            </v-autocomplete>
          </v-col>
        </v-row>
      </div>
    </material-card>    
  </v-dialog>
</template>

<script>
  export default {
    components: {
      SetupBranchChartBarSkillChangeOverview: () => import('@/components/setup/branch/ChartBarSkillChangeOverview'),
      SetupBranchChartRadarSkillChangeOverview: () => import('@/components/setup/branch/ChartRadarSkillChangeOverview'),
    },
    props: {
      branch: {
        type: Object,
        default: () => ({}),
      },
      branchList: {
        type: Array,
        default: () => {
          return []
        },
      },
      selectedSkill: {
        type: Number,
        default: 1,
      },
    },
    data () {
      return {
        dialog: false,
        employeeSkillFilter: undefined,
        branchSkills: [],
        employeeList: [],
        temporarySelectedEmployees: [],
        selectedEmployees: [],
        chartSkillFilter: [],
        secondaryBranch: undefined,
        chartSwitch: 'bar',
        disableRadar: true,
      
      }
    },
    computed: {
      highestYValue () {
        let highestValue = 0

        this.barchart1Data[0].data.forEach((value, index) => {
          if (value + this.barchart1Data[1].data[index] > highestValue) {
            highestValue = value + this.barchart1Data[1].data[index]
          }
        })

        if (this.barchart2Data) {
          this.barchart2Data[0].data.forEach((value, index) => {
            if (value + this.barchart2Data[1].data[index] > highestValue) {
              highestValue = value + this.barchart2Data[1].data[index]
            }
          })
        }
      
        return highestValue
      },
      employeesLeftAfterTransfer () {
        return this.employeeList.filter((employee) => {
          let found = this.selectedEmployees.find(selectedEmployee => selectedEmployee.person_uid === employee.person_uid)
          if (found) {
            return false
          } 
          return true
        })
      },
      filteredEmployeesLeft () {
        return this.filteredEmployees.filter((employee) => {
          let found = this.selectedEmployees.find(selectedEmployee => selectedEmployee.person_uid === employee.person_uid)
          if (found) {
            return false
          } 
          return true
        })
      },
      chartLabels () {
        return this.aggregateFilteredSelectedEmployeeSkills.map((skill) => {
          return skill.name
        })
      },
      radarLabels () {
        return this.chartSkillFilter.map((skill) => {
          return skill.name
        })
      },
      barchart1Data () {
        let data = [{
                      label: 'After',
                      data: [],
                      order: 0,
                      maxBarThickness: 100, 
                    },
                    {
                      label: 'Before',
                      data: [],
                      order: 1,
                      maxBarThickness: 100, 
                    }]

        let branchSkills = this.aggregateBranchSkills

        this.aggregateFilteredSelectedEmployeeSkills.forEach((skill) => {
          let activeSkill = branchSkills.find((branchSkill) => branchSkill.skill_id === skill.skill_id)

          if (activeSkill) {
            data[0].data.push(activeSkill.total - skill.total)
          } else {
            data[0].data.push(0)
          }
        
          data[1].data.push(skill.total)
        })
     
        return data
      },
      barchart2Data () {
        if (!this.secondaryBranch) return undefined
        let data = [{
                      label: 'After',
                      data: [],
                      order: 1,
                      maxBarThickness: 100, 
                    },
                    {
                      label: 'Before',
                      data: [],
                      order: 0,
                      maxBarThickness: 100, 
                    }]

        let branchSkills = this.aggregateSecondaryBranchSkills      

        this.aggregateFilteredSelectedEmployeeSkills.forEach((skill) => {
          let activeSkill = branchSkills.find((branchSkill) => branchSkill.skill_id === skill.skill_id)
          if (!activeSkill) {
            data[1].data.push(0)
          } else {
            data[1].data.push(activeSkill.total)
          }
          data[0].data.push(skill.total)
        })
        return data
      },
      radar1Data () {
        let data = [
          { 
            label: 'After',
            data: [],
            fill: true,
            backgroundColor: 'rgba(235, 47, 47, 0.2)',
            borderColor: 'rgb(235, 47, 47)',
            pointBackgroundColor: 'rgb(235, 47, 47)', 
            pointHoverBorderColor: 'rgb(235, 47, 47)', 
          },
          { 
            label: 'Before',
            data: [],
            fill: true,
            backgroundColor: 'rgba(170, 179, 175, 0.6)',
            borderColor: 'rgb(170, 179, 175)',
          },
        ] 

        let newBranchSkills = this.summarizeSkills(this.employeesLeftAfterTransfer)

        this.chartSkillFilter.forEach((skill) => {
          let activeSkill = this.branchSkills.find(activeSkill => activeSkill.skill_id === skill.skill_id)
          let newActiveSkill = newBranchSkills.find(activeSkill => activeSkill.skill_id === skill.skill_id)

          if (newActiveSkill) {
            data[0].data.push(newActiveSkill.highest_available_employee_level)
          } else {
            data[0].data.push(0)
          }
        
          data[1].data.push(activeSkill.highest_available_employee_level)
        })     
      
        return data
      },
      radar2Data () {
        let data = [
          { 
            label: 'After',
            data: [],
            fill: true,
            backgroundColor: 'rgba(104, 237, 157, 0.2)',
            borderColor: 'rgb(104, 237, 157)',
            pointBackgroundColor: 'rgb(104, 237, 157)', 
            pointHoverBorderColor: 'rgb(104, 237, 157)', 
          },
          { 
            label: 'Before',
            data: [],
            fill: true,
            backgroundColor: 'rgba(170, 179, 175, 0.6)',
            borderColor: 'rgb(170, 179, 175)',
          },
        ] 

        if (!this.secondaryBranch) {
          return data
        }
      
        let branchUsers = [...this.secondaryBranch.active_users]
        let originalBranchSkills = this.summarizeSkills(branchUsers)
        let newUserList = [...branchUsers, ...this.selectedEmployees]
        let newBranchSkills = this.summarizeSkills(newUserList)

        this.chartSkillFilter.forEach((skill) => {
          let activeSkill = originalBranchSkills.find(activeSkill => activeSkill.skill_id === skill.skill_id)
          let newActiveSkill = newBranchSkills.find(activeSkill => activeSkill.skill_id === skill.skill_id)

          if (newActiveSkill) {
            data[0].data.push(newActiveSkill.highest_available_employee_level)
          } else {
            data[0].data.push(0)
          }

          if (activeSkill) {
            data[1].data.push(activeSkill.highest_available_employee_level)
          } else {
            data[1].data.push(0)
          }
        })   

        return data
      },
      aggregateSecondaryBranchSkills () {
        let branchSkills = []

        if (!this.secondaryBranch) return []
      
        this.secondaryBranch.active_users.forEach((user) => {
          if (((user.person || {}).employee || {}).skills !== undefined) {
            if (user.person.employee.skills.length > 0) {
              let skills = user.person.employee.skills
            
              skills.forEach((skill) => {
                let activeSkill = branchSkills.find((branchSkill) => branchSkill.skill_id === skill.skill_id)
              
                if (activeSkill) {
                  activeSkill.total += skill.skill_level.level
                  activeSkill.levels[skill.skill_level.level - 1].push(user)
                } else {
                  let levels = [[], [], [], [], []]

                  levels[skill.skill_level.level - 1].push(user)
                
                  branchSkills.push({
                    skill_id: skill.skill_id,
                    name: skill.skill.name,
                    image_url: skill.skill.image_url,
                    total: skill.skill_level.level,
                    levels: levels,
                  })
                }
              })
            }
          }
        })
      
        return branchSkills
          .filter((skill) => {
            let activeSkill = this.chartSkillFilter.find((branchSkill) => branchSkill.skill_id === skill.skill_id)
            if (activeSkill) {
              return true
            }
            return false
          })
          .sort((a, b) => {
            return a.name > b.name ? -1 : 1
          })
      },
      aggregateBranchSkills () {
        let branchSkills = []
      
        this.employeeList.forEach((user) => {
          if (((user.person || {}).employee || {}).skills !== undefined) {
            if (user.person.employee.skills.length > 0) {
              let skills = user.person.employee.skills
            
              skills.forEach((skill) => {
                let activeSkill = branchSkills.find((branchSkill) => branchSkill.skill_id === skill.skill_id)
              
                if (activeSkill) {
                  activeSkill.total += skill.skill_level.level
                  activeSkill.levels[skill.skill_level.level - 1].push(user)
                } else {
                  let levels = [[], [], [], [], []]

                  levels[skill.skill_level.level - 1].push(user)
                
                  branchSkills.push({
                    skill_id: skill.skill_id,
                    name: skill.skill.name,
                    image_url: skill.skill.image_url,
                    total: skill.skill_level.level,
                    levels: levels,
                  })
                }
              })
            }
          }
        })
      
        if (this.chartSkillFilter.length === 0) {
          return branchSkills
        }
      
        return this.filterAndSortSelectedSkills(branchSkills)
      },
      aggregateFilteredSelectedEmployeeSkills () {
        return this.filterAndSortSelectedSkills(this.aggregateSelectedEmployeeSkills)
      },
      aggregateSelectedEmployeeSkills () {
        let employeeSkills = []
      
        this.selectedEmployees.forEach((user) => {
          if (((user.person || {}).employee || {}).skills !== undefined) {
            if (user.person.employee.skills.length > 0) {
              let skills = user.person.employee.skills
            
              skills.forEach((skill) => {
                let activeSkill = employeeSkills.find((branchSkill) => branchSkill.skill_id === skill.skill_id)
              
                if (activeSkill) {
                  activeSkill.total += skill.skill_level.level
                  activeSkill.levels[skill.skill_level.level - 1].push(user)
                } else {
                  let levels = [[], [], [], [], []]

                  levels[skill.skill_level.level - 1].push(user)
                
                  employeeSkills.push({
                    skill_id: skill.skill_id,
                    name: skill.skill.name,
                    image_url: skill.skill.image_url,
                    total: skill.skill_level.level,
                    levels: levels,
                  })
                }
              })
            }
          }
        })
      
        return employeeSkills
      },
      filteredEmployees () {
        let employees = [...this.employeeList]  
      
        employees = employees.filter((employee) => {
          let skills = (((((employee || {}).person || {}).employee) || {}).skills || [])
          if (skills.length === 0) {
            if (!this.employeeSkillFilter) {
              return true
            }
            return false
          }

          let meetAllRequirements = true

          if (this.employeeSkillFilter) {
            let employeeActiveSkill = skills.find((employeeSkill) => employeeSkill.skill_id === this.employeeSkillFilter.skill_id)
            if (employeeActiveSkill) {
              if (employeeActiveSkill.skill_level.level < this.employeeSkillFilter.rating) {
                meetAllRequirements = false
              } else {
                employee.rating = employeeActiveSkill.skill_level.level
              }
            } else {
              meetAllRequirements = false
            }
          }
        
          return meetAllRequirements
        })
      
        return employees
      },
    },
    watch: {
      dialog: {
        handler (value) {
          if (value === true) {
            let found = this.branchSkills.find(skill => skill.skill_id === this.selectedSkill)
            if (found) {            
              this.employeeSkillFilter = found
            }
          }
        },
      },
      branch: {
        immediate: true,
        handler (value) {
          if (value) {
            let skills = [...value.skills]
            skills.forEach((skill) => {
              skill.rating = 1
            })
            this.branchSkills = skills
            this.employeeList = [...value.active_users]
          }
        },
      },
      selectedEmployees: {
        handler () {
          this.populateSkillFilter(this.aggregateSelectedEmployeeSkills)
        },
      },
      chartSkillFilter: {
        handler (value) {
          if (value) {
            if (value.length >= 3) {
              this.disableRadar = false
            } else {
              this.chartSwitch = 'bar'
              this.disableRadar = true
            }
          }
        },
      },
    },
    methods: {
      summarizeSkills (users) {
        let branchSkills = []

        users.forEach((user) => {
          if (((user.person || {}).employee || {}).skills !== undefined) {
            if (user.person.employee.skills.length > 0) {
              let skills = user.person.employee.skills
            
              skills.forEach((skill) => {
                let activeSkill = branchSkills.find((branchSkill) => branchSkill.skill_id === skill.skill_id)
              
                if (activeSkill) {
                  activeSkill.count += 1
                  activeSkill.levels[skill.skill_level.level - 1].push(user)

                  if (activeSkill.highest_available_employee_level < skill.skill_level.level) {
                    activeSkill.highest_available_employee_level = skill.skill_level.level
                  }
                } else {
                  let levels = [[], [], [], [], []]

                  levels[skill.skill_level.level - 1].push(user)
                
                  branchSkills.push({
                    skill_id: skill.skill_id,
                    highest_available_employee_level: skill.skill_level.level,
                    count: 1,
                    name: skill.skill.name,
                    image_url: skill.skill.image_url,
                    levels: levels,
                  })
                }
              })
            }
          }
        })
      
        return branchSkills.sort((a, b) => {
          return a.highest_available_employee_level > b.highest_available_employee_level ? -1 : 1
        })
      },
      filterAndSortSelectedSkills (skills) {
        return skills
          .filter((skill) => {
            let activeSkill = this.chartSkillFilter.find((branchSkill) => branchSkill.skill_id === skill.skill_id)
            if (activeSkill) {
              return true
            }
            return false
          })
          .sort((a, b) => {
            return a.name > b.name ? -1 : 1
          })
      },
      closeDialog () {
        this.selectedEmployees = []
        this.dialog = false
      },
      addToEmployeeList () {
        this.selectedEmployees = [...new Set([...this.selectedEmployees, ...this.temporarySelectedEmployees])]
        this.temporarySelectedEmployees = []
      },
      populateSkillFilter (data) {
        this.chartSkillFilter = [...data]
      },
      removeTempSelectedEmployees (item) {
        let index = this.temporarySelectedEmployees.findIndex(selected => selected.person_uid === item.person_uid)
        if (index >= 0) this.temporarySelectedEmployees.splice(index, 1)
      },
      removeSelectedEmployees (item) {
        let index = this.selectedEmployees.findIndex(selected => selected.person_uid === item.person_uid)
        if (index >= 0) this.selectedEmployees.splice(index, 1)
      },
      removeSelectedChartSkillFilter (item) {
        let index = this.chartSkillFilter.findIndex(selected => selected.skill_id === item.skill_id)
        if (index >= 0) this.chartSkillFilter.splice(index, 1)
      },
      removeEmployeeSkillFilter (item) {
        let itemIndex = this.branchSkills.findIndex(selected => selected.skill_id === item.skill_id)
      
        this.$set(this.branchSkills[itemIndex], 'rating', 1)

        this.employeeSkillFilter = undefined
      },
    },
  }
</script>

<style lang="css" scoped>
  .overflow {
    height:75vh;
    overflow-x:hidden;
  }

  .btn-group{
    height:40px !important
  }
</style>
